"use client";

import { useState } from "react";
import { presentRewardLabel } from "@/lib/presents";
import type { PresentsResponse } from "@/app/api/presents/route";
import { useUiTexts } from "@/lib/ui-texts-client";
import windowCss from "./LoginBonusWindow.module.css";

/** 判の傾き。日ごとに少しずつ変える */
const tilt = (day: number) => ((day * 37) % 9) - 4;

/** 判のインクのかすれ（縁を少し揺らし、ところどころ少しだけ薄くする。10/22 本人「印字が薄い」で薄くする量を減らした）。カードに1つだけ置き、判の SVG から参照する（ホームの貸出カードの窓 LoginBonusWindow でも使う） */
export function InkFilter() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <filter id="loan-card-ink" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" seed="3" result="grain" />
        <feDisplacementMap in="SourceGraphic" in2="grain" scale="1.4" xChannelSelector="R" yChannelSelector="G" result="rough" />
        <feTurbulence type="fractalNoise" baseFrequency="0.28" numOctaves="2" seed="11" result="patch" />
        <feColorMatrix in="patch" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  -0.9 0 0 0 1.5" result="mask" />
        <feComposite in="rough" in2="mask" operator="in" />
      </filter>
    </svg>
  );
}

/** 日付の判（紫のインクのゴム判。10/11 → 10.11）。framed は今日の判＝朱の枠付き（ホームの貸出カードの窓） */
export function DateStamp({ text, day, framed = false }: { text: string; day: number; framed?: boolean }) {
  const ink = framed ? "#b5372c" : "#3a2f80";
  return (
    <svg width="54" height="22" viewBox="0 0 54 22" role="img" aria-label={text} style={{ transform: `rotate(${tilt(day)}deg)` }}>
      <g filter="url(#loan-card-ink)">
        {framed && <rect x="1.5" y="1" width="51" height="20" rx="2" fill="none" stroke={ink} strokeWidth={1.6} />}
        <text x="27" y="16" textAnchor="middle" style={{ fontFamily: "var(--font-mincho)" }} fontWeight={800} fontSize={14} fill={ink} letterSpacing={0.5}>
          {text.replace("/", ".")}
        </text>
      </g>
    </svg>
  );
}

/** 日付を残す前にログインした日の判（朱の丸印） */
export function DoneSeal({ text, day }: { text: string; day: number }) {
  // 文言で「使わない」にしたときは判ごと出さない
  if (!text) return null;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" role="img" aria-label={text} style={{ transform: `rotate(${tilt(day) * 2}deg)` }}>
      <g filter="url(#loan-card-ink)">
        <circle cx="12" cy="12" r="10" fill="none" stroke="#b5372c" strokeWidth={1.6} />
        <text x="12" y="16.2" textAnchor="middle" style={{ fontFamily: "var(--font-mincho)" }} fontWeight={700} fontSize={12} fill="#b5372c">
          {text}
        </text>
      </g>
    </svg>
  );
}

/** 受け取った日の「受領」の判（朱の二重枠の角判。届くものの欄の右端に重ねて押す・10/23）。press は押したばかりの「ポン」 */
export function ReceivedStamp({ text, day, press = false }: { text: string; day: number; press?: boolean }) {
  // 文言で「使わない」にしたときは判ごと出さない
  if (!text) return null;
  return (
    <span className={`pointer-events-none absolute right-1 top-1/2 flex -translate-y-1/2 mix-blend-multiply ${press ? windowCss.press : ""}`}>
      <svg width="42" height="22" viewBox="0 0 42 22" role="img" aria-label={text} style={{ transform: `rotate(${-tilt(day) - 6}deg)` }}>
        <g filter="url(#loan-card-ink)">
          <rect x="1" y="1" width="40" height="20" rx="3" fill="none" stroke="#b5372c" strokeWidth={1.6} />
          <rect x="3.5" y="3.5" width="35" height="15" rx="2" fill="none" stroke="#b5372c" strokeWidth={0.8} />
          <text x="21" y="15.5" textAnchor="middle" style={{ fontFamily: "var(--font-mincho)" }} fontWeight={800} fontSize={11.5} fill="#b5372c" letterSpacing={1.5}>
            {text}
          </text>
        </g>
      </svg>
    </span>
  );
}

/** 今日の日目を鉛筆で丸く囲む */
export function PencilRing() {
  return (
    <svg viewBox="0 0 30 22" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[22px] w-[30px] -translate-x-1/2 -translate-y-1/2 -rotate-[8deg]">
      <path d="M4 12 C4 4, 26 2, 27 10 C28 18, 8 21, 4 13 C3 9, 9 5, 16 5" fill="none" stroke="#4d4640" strokeOpacity={0.7} strokeWidth={1.2} strokeLinecap="round" />
    </svg>
  );
}

/**
 * ログインボーナスを図書館の貸出カードの形で見せる（「報酬一覧・ログインボーナス」の画面のタブ。10/12 にプレゼントの画面から移した）。ログインした日ごとに日付の判を押していく（10日で1枚）。
 * 届くものはその日のログイン報酬。カードより先に届く日は下に小さく出す。
 * 紙を貼らず、厚紙のカード（goals の .library-notebook[data-paper="kraft"]）に直接書く（10/12 本人「紙に図書カードを貼ったようになっている」）。行の高さは罫線の間隔（28px）にそろえ、横の線はカードの印刷の罫線に任せる。
 * 10/18 本人が見本の案Aを選んだ: 字と数字は明朝、日付は紫のインクのかすれた判、「済」は朱の丸印、今日は黄色の帯をやめて鉛筆の丸で日目を囲む
 * 10/23 本人「自動で手に入ってしまう・手に入れた実感がない」: まだ受け取っていない日の届くものはボタンになり、押すとその場で受け取る（onClaim）。
 * 前のカードの日で受け取っていない分は、カードの上にまとめて出す
 */
export default function LoginBonusCard({ login, onClaim }: { login: PresentsResponse["login"]; onClaim?: (ids: string[]) => Promise<boolean> }) {
  const { t } = useUiTexts();
  const [claimingDay, setClaimingDay] = useState<number | null>(null);
  /** いま受け取った日（その行の「受領」の判をポンと押す） */
  const [justClaimed, setJustClaimed] = useState<number | null>(null);
  const [error, setError] = useState(false);
  const lastRow = login.card[login.card.length - 1]?.day ?? login.days;
  const later = login.upcoming.filter((row) => row.day > lastRow);
  const pendingOf = (day: number) => login.pending.find((p) => p.day === day);
  // カードに行の無い日（前のカード・日の分からないもの）の受け取り待ち
  const firstRow = login.card[0]?.day ?? 1;
  const offCard = login.pending.filter((p) => p.day < firstRow || p.day > lastRow);

  async function claim(day: number, ids: string[]) {
    if (!onClaim || claimingDay !== null) return;
    setClaimingDay(day);
    setError(false);
    const ok = await onClaim(ids);
    if (ok) setJustClaimed(day);
    else setError(true);
    setClaimingDay(null);
  }

  /** 届くもののボタン（朱の細い枠。罫線の高さを変えないよう枠は内側の影で引く） */
  const claimButton = (day: number, ids: string[], labels: string[]) => (
    <button
      type="button"
      onClick={() => claim(day, ids)}
      disabled={claimingDay !== null}
      className="-mx-1 rounded-[3px] px-1 text-left shadow-[inset_0_0_0_1.5px_#b5372c99] transition-colors hover:bg-[#b5372c14] active:bg-[#b5372c26] disabled:opacity-60"
    >
      {labels.map((label, i) => (
        <span key={i} className="whitespace-nowrap">{label}{i < labels.length - 1 ? "、" : ""}</span>
      ))}
      <span className="ml-1.5 whitespace-nowrap text-[11px] font-bold text-[#b5372c]">
        {claimingDay === day ? t("quests.presents.loginCard.claiming") : t("quests.presents.loginCard.claim")}
      </span>
    </button>
  );
  return (
    // 頁の上の余白（14px）に足して、表の頭を罫線の行（28px の倍数）にそろえる
    <section className="mt-[14px] font-mincho text-[#3b2a19] [&_*]:leading-[28px]">
      <InkFilter />
      {/* 見出しは上のタブ（ログインボーナス）と重なるので出さず、累計の日数を最上段の罫線の行に置く（10/12 本人の指示） */}
      <div className="flex h-[28px] items-end justify-end pr-1">
        <p className="text-sm">
          <span className="mr-1 text-xs text-[#7a5a35]">{t("quests.presents.login.total")}</span>
          <span className="text-[22px] font-bold tabular-nums">{login.days}</span>
          <span className="ml-0.5 text-xs text-[#7a5a35]">{t("quests.presents.login.days")}</span>
        </p>
      </div>
      {error && <p className="text-xs text-[#b5372c]">{t("quests.presents.error")}</p>}
      {offCard.length > 0 && (
        <div className="mb-[28px]">
          <p className="text-[10px] tracking-[0.2em] text-[#8a4a35]">{t("quests.presents.loginCard.pending")}</p>
          <ul className="flex flex-col text-xs">
            {offCard.map((p) => (
              <li key={p.day} className="flex items-start gap-3 font-semibold">
                {p.day > 0 && <span className="w-14 shrink-0">{t("quests.presents.login.day", { 日数: p.day })}</span>}
                <span className="min-w-0">{claimButton(p.day, p.ids, p.labels)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div role="table" className="text-xs">
        <div role="row" className="grid h-[28px] grid-cols-[3rem_4.75rem_1fr] text-[11px] tracking-[0.2em] text-[#8a4a35]">
          <span role="columnheader" className="px-2 text-center">{t("quests.presents.loginCard.colDay")}</span>
          <span role="columnheader" className="border-l border-[#9a4a3a55] px-2 text-center">{t("quests.presents.loginCard.colDate")}</span>
          <span role="columnheader" className="border-l border-[#9a4a3a55] px-2">{t("quests.presents.loginCard.colRewards")}</span>
        </div>
        {login.card.map((row) => (
          <div
            key={row.day}
            role="row"
            className="grid min-h-[28px] grid-cols-[3rem_4.75rem_1fr] items-stretch"
          >
            <span role="cell" className="px-2 text-center text-[14px] font-semibold tabular-nums">
              {/* 丸は数字に合わせる（届くものが折り返して行が高くなっても、数字は1行目に残るので、行の真ん中に置くとずれる・10/22） */}
              <span className="relative inline-block">
                {row.day}
                {row.day === login.days && <PencilRing />}
              </span>
            </span>
            {/* 判も日目の数字と同じ1行目に押す（届くものが折り返して行が高くなっても下がらない） */}
            <span role="cell" className="flex justify-center border-l border-[#9a4a3a55]">
              <span className="flex h-[28px] items-center">
                {row.stamped && (row.date ? <DateStamp day={row.day} text={row.date} /> : <DoneSeal day={row.day} text={t("quests.presents.loginCard.noDate")} />)}
              </span>
            </span>
            {/* 届いた日の行も薄くしない（10/22 本人「届いたアイテムの表示をもっと濃く」）。届いたかは隣の日付の判で分かる */}
            {/* 受け取った日は右端に「受領」の判を重ねるので、品の字が判の下に潜らないよう右を空ける */}
            <span role="cell" className={`relative border-l border-[#9a4a3a55] pl-2 font-semibold ${row.received ? "pr-12" : "pr-2"}`}>
              {/* ログイン報酬と、報酬一覧の「ログイン日数」のノードの報酬（10/22）を並べる。まだ受け取っていない日はボタン */}
              {(() => {
                const labels = [...row.rewards.map(presentRewardLabel), ...row.goalItems.map((i) => i.label)];
                const pending = pendingOf(row.day);
                // 受け取り待ちの日は、実際に届いている中身を出す（届いた後に管理UIで表を変えても、押して入るものと食い違わない）
                if (pending) return claimButton(row.day, pending.ids, pending.labels);
                return labels.length > 0
                  ? labels.map((label, i, all) => (
                      // 1つの品の途中（「コンパス」と「×1」の間など）では折り返さない
                      <span key={i} className="whitespace-nowrap">{label}{i < all.length - 1 ? "、" : ""}</span>
                    ))
                  : <span className="opacity-40">{t("quests.presents.loginCard.none")}</span>;
              })()}
              {row.received && <ReceivedStamp day={row.day} text={t("quests.presents.loginCard.received")} press={justClaimed === row.day} />}
            </span>
          </div>
        ))}
      </div>
      {later.length > 0 && (
        <div className="mt-[28px]">
          <p className="text-[10px] tracking-[0.2em] text-[#7a5a35]">{t("quests.presents.loginCard.later")}</p>
          <ul className="flex flex-col">
            {later.map((row) => (
              <li key={row.day} className="flex items-start gap-3 text-xs">
                <span className="w-14 shrink-0 font-semibold">{t("quests.presents.login.day", { 日数: row.day })}</span>
                <span className="min-w-0">{row.rewards.map(presentRewardLabel).join("、")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
